import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse } from '../../types';
import { parseQueryParams } from '../../utils';
import { baseQueryWithReauth } from './baseQuery';

export enum MasterConfigType {
  MATERIAL = 'MATERIAL',
  CUSTOMER = 'CUSTOMER',
  VENDOR = 'VENDOR',
}

export interface MasterColumnConfigItem {
  key: string;
  label: string;
  visible?: boolean;
  order: number;
}

export interface MasterFieldConfigDto {
  data: MasterColumnConfigItem[];
}

export interface CreateMasterTableConfigDto {
  name: string;
  type: MasterConfigType;
  fieldConfigJson: MasterFieldConfigDto;
  isDefault?: boolean;
}

export interface UpdateMasterTableConfigDto {
  name?: string;
  type?: MasterConfigType;
  fieldConfigJson?: MasterFieldConfigDto;
  isDefault?: boolean;
}

export interface ApplyMasterTableConfigDto {
  configId: number;
  page: MasterConfigType;
}

export interface MasterTableConfig {
  id: number;
  name: string;
  type?: MasterConfigType | string;
  fieldConfigJson?: MasterFieldConfigDto | MasterColumnConfigItem[] | { data: MasterColumnConfigItem[] } | any;
  columns?: MasterColumnConfigItem[];
  data?: MasterColumnConfigItem[];
  isDefault?: boolean;
  createdAt?: string;
  updatedAt?: string;
  createdByUser?: {
    ID: number;
    FullName: string;
    EmailID: string;
  };
}

export function normalizeColumnItem(item: any, idx: number): MasterColumnConfigItem {
  if (!item || typeof item !== 'object') {
    return { key: String(item), label: String(item), order: idx + 1, visible: true };
  }
  return {
    key: item.key || item.field || item.columnKey || item.name || '',
    label: item.label || item.headerName || item.header || item.title || item.key || item.field || '',
    order: item.order !== undefined ? item.order : (item.idx !== undefined ? item.idx : idx + 1),
    visible: item.visible !== false && item.hide !== true,
  };
}

export function parseMasterFieldConfigJson(
  fieldConfigJson: unknown,
): { data: MasterColumnConfigItem[] } | undefined {
  if (fieldConfigJson == null) return undefined;

  let parsed: unknown = fieldConfigJson;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return undefined;
    }
  }

  if (Array.isArray(parsed)) {
    return { data: parsed.map((item, idx) => normalizeColumnItem(item, idx)) };
  }

  if (
    parsed &&
    typeof parsed === 'object' &&
    Array.isArray((parsed as { data?: unknown }).data)
  ) {
    return { data: ((parsed as { data: any[] }).data).map((item, idx) => normalizeColumnItem(item, idx)) };
  }

  return undefined;
}

export function getMasterTableConfigColumns(
  config?: MasterTableConfig | null,
): MasterColumnConfigItem[] {
  if (!config) return [];

  const fieldConfig =
    parseMasterFieldConfigJson(config.fieldConfigJson) ||
    (Array.isArray(config.columns) ? { data: config.columns } : undefined) ||
    (Array.isArray(config.data) ? { data: config.data } : undefined);

  return [...(fieldConfig?.data ?? [])]
    .filter(column => column.visible !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

export function extractMasterActiveConfigId(raw: unknown): number | undefined {
  if (!raw || typeof raw !== 'object') return undefined;

  const record = raw as Record<string, unknown>;
  if (typeof record.configId === 'number') return record.configId;
  if (typeof record.tableConfigId === 'number') return record.tableConfigId;

  if (record.config && typeof record.config === 'object') {
    const config = record.config as Record<string, unknown>;
    if (typeof config.id === 'number') return config.id;
  }

  if (typeof record.id === 'number') return record.id;
  return undefined;
}

export function normalizeMasterTableConfig(
  raw: unknown,
): MasterTableConfig | null {
  if (!raw || typeof raw !== 'object') return null;

  const record = raw as Record<string, unknown>;
  const nested =
    record.config && typeof record.config === 'object'
      ? (record.config as MasterTableConfig)
      : (raw as MasterTableConfig);

  const fieldConfigJson =
    parseMasterFieldConfigJson(nested.fieldConfigJson) ||
    (Array.isArray(nested.columns)
      ? { data: nested.columns }
      : undefined) ||
    (Array.isArray(nested.data) ? { data: nested.data } : undefined);

  if (!fieldConfigJson?.data?.length) {
    const id = extractMasterActiveConfigId(raw);
    if (id) {
      return { ...nested, id };
    }
    return null;
  }

  return {
    ...nested,
    fieldConfigJson,
    columns: nested.columns ?? fieldConfigJson.data,
  };
}

export const masterTableConfigApi = createApi({
  reducerPath: 'masterTableConfig',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['MasterTableConfig'],
  endpoints: builder => ({
    getMasterTableConfigList: builder.query<
      BackendResponse<MasterTableConfig[]>,
      { page?: MasterConfigType | string } | void
    >({
      query: params => ({
        url: `/master-table-config${params ? parseQueryParams(params) : ''}`,
        method: 'GET',
      }),
      providesTags: ['MasterTableConfig'],
    }),

    getActiveMasterTableConfig: builder.query<
      BackendResponse<MasterTableConfig>,
      { page: MasterConfigType | string }
    >({
      query: ({ page }) => ({
        url: `/master-table-config/active${parseQueryParams({ page })}`,
        method: 'GET',
      }),
      providesTags: ['MasterTableConfig'],
    }),

    getMasterTableConfig: builder.query<
      BackendResponse<MasterTableConfig>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/master-table-config/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { id }) => [
        { type: 'MasterTableConfig', id },
      ],
    }),

    createMasterTableConfig: builder.mutation<
      BackendResponse<MasterTableConfig>,
      CreateMasterTableConfigDto
    >({
      query: payload => ({
        url: '/master-table-config',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['MasterTableConfig'],
    }),

    updateMasterTableConfig: builder.mutation<
      BackendResponse<MasterTableConfig>,
      { id: number; data: UpdateMasterTableConfigDto }
    >({
      query: ({ id, data }) => ({
        url: `/master-table-config/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['MasterTableConfig'],
    }),

    deleteMasterTableConfig: builder.mutation<
      BackendResponse<void>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/master-table-config/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['MasterTableConfig'],
    }),

    applyMasterTableConfig: builder.mutation<
      BackendResponse<void>,
      ApplyMasterTableConfigDto
    >({
      query: payload => ({
        url: '/master-table-config/apply',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['MasterTableConfig'],
    }),
  }),
});

export const {
  useGetMasterTableConfigListQuery,
  useGetActiveMasterTableConfigQuery,
  useGetMasterTableConfigQuery,
  useLazyGetMasterTableConfigQuery,
  useCreateMasterTableConfigMutation,
  useUpdateMasterTableConfigMutation,
  useDeleteMasterTableConfigMutation,
  useApplyMasterTableConfigMutation,
} = masterTableConfigApi;

export default masterTableConfigApi;
